/**
 * @typedef {import('./Response').default} Response
 * @typedef {import('../../../../App/shared/OfferService/OfferFields').default} VendorFields
 */

import { offerService } from '../../../../service-container';
import { useEffect, useMemo, useRef, useState } from 'react';
import captureError from '../../../../utils/captureError';

/**
 * @param {'agent' | 'group' | 'tenant'} mode
 * @param {number} [pageNumber]
 * @param {boolean} [isGated]
 * @returns {Response}
 */
export default function useGetAdminVendors(
	mode = 'agent',
	pageNumber = 1,
	isGated = false
) {
	const [isGettingData, setIsGettingData] = useState(true);
	const [lastPageNumber, setLastPageNumber] = useState(1);
	const [total, setTotal] = useState(0);
	const [vendors, setVendors] = useState(/** @type {VendorFields[]} */ ([]));
	const inFlightKeyRef = useRef(/** @type {string | null} */ (null));
	const requestSequenceRef = useRef(0);

	useEffect(() => {
		if (isGated) {
			setIsGettingData(false);
			return;
		}

		fetchData();
	}, [isGated, mode, pageNumber]);

	async function fetchData() {
		const requestKey = `${mode}:${pageNumber}:${isGated}`;
		if (inFlightKeyRef.current === requestKey) {
			return;
		}

		inFlightKeyRef.current = requestKey;
		const requestSequence = requestSequenceRef.current + 1;
		requestSequenceRef.current = requestSequence;
		setIsGettingData(true);

		try {
			let response;

			if (mode === 'group') {
				response = await offerService.getGroupOffers(pageNumber);
			} else if (mode === 'tenant') {
				response = await offerService.getDomainOffers(pageNumber, {
					relatedType: 'tenant',
				});
			} else {
				response = await offerService.getDomainOffers(pageNumber, {
					relatedType: 'agent',
				});
			}

			if (requestSequence === requestSequenceRef.current) {
				setLastPageNumber(response.pages ?? 1);
				setVendors(response.offers);
				setTotal(response.total ?? 0);
			}
		} catch (error) {
			captureError(error);
		} finally {
			if (inFlightKeyRef.current === requestKey) {
				inFlightKeyRef.current = null;
			}

			if (requestSequence === requestSequenceRef.current) {
				setIsGettingData(false);
			}
		}
	}

	return useMemo(
		() => ({
			data: vendors,
			isGettingData,
			lastPageNumber,
			total,
		}),
		[vendors, isGettingData, lastPageNumber, total]
	);
}
